import { useAuthentication } from '@/composables/useAuthentication';
import { isJsonLayerResponse } from '@/interfaces/jsonLayerResponse';

interface UpdateChartOptions {
  chartName?: string;
  comments?: string;
  annotations?: string;
  styleUrlSearchParams?: URLSearchParams;
};

export async function updateChart(recid: number, options: UpdateChartOptions = {}): Promise<void> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to update chart - user is not logged in.');
  }

  const chartCodeCopy = chartCode.value;
  const urlSearchParams = new URLSearchParams();

  // want to allow optionals to update only what we need
  const appendOptionalUrlSearchParam = (key: string, value?: string) => {
    if (typeof value === 'undefined')
      return;

    urlSearchParams.set(key, value);
  }

  const { chartName, comments, annotations, styleUrlSearchParams } = options;

  let style: string | undefined = undefined;

  if (styleUrlSearchParams instanceof URLSearchParams) {
    style = styleUrlSearchParams.toString();

    if (!style) {
      throw new Error('Unable to update chart - styleUrlSearchParams is empty.');
    }

    style = '&' + style;
  }

  urlSearchParams.set('cmd', 'update-favorite');
  urlSearchParams.set('chartcode', chartCodeCopy);
  urlSearchParams.set('recid', recid.toString());
  appendOptionalUrlSearchParam('comments', comments);
  appendOptionalUrlSearchParam('annotations', annotations);
  appendOptionalUrlSearchParam('style', style);
  appendOptionalUrlSearchParam('desc', chartName);

  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: urlSearchParams,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info('update chart jsonLayerResponse', jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to update chart with recid: ${recid}.`, e);

    throw new Error(`Failed to update chart with recid: ${recid}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to update chart with recid: ${recid} for chartCode: ${chartCodeCopy} and chartName: ${chartName} and style: ${style} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to update chart with recid: ${recid} for chartCode: ${chartCodeCopy} and chartName: ${chartName} and style: ${style} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}
